import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../services/apiService';
import CustomSelect from '../../../common/formfields/CustomSelect';
import { primaryColor, secondaryColor } from '../../../../utils/comon';
import storageService from '../../../../utils/storageService';
import Breadcrumbs from '../../../common/BreadCrum/BreadCrum';

const PollForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    
    const isEdit = id && id !== 'add';
    const pollIdToEdit = isEdit ? id : null;
    
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(isEdit);

    const [userId, setUserId] = useState(0);
    const [siteId, setSiteId] = useState(0);

    const [formData, setFormData] = useState({
        pollId: 0,
        optionId: 0,
        title: '',
        description: '',
        pollType: '',
        startDate: '',
        endDate: '',
        isAnonymous: false,
        status: null,
        showResultsLive: false
    });

    const [pollOptions, setPollOptions] = useState(['', '']);

    useEffect(() => {
        const fetchContext = async () => {
            const employeeCode = storageService.getItem('employeeCode');
            if (employeeCode) {
                try {
                    const userRes = await apiServices({
                        url: '/ResidentialUser/GetResidentialUserById',
                        method: 'POST',
                        data: { userId: employeeCode }
                    });
                    if (userRes.success || userRes.isSuccess || userRes) {
                        const apiData = userRes.data || userRes;
                        const user = (apiData?.data && apiData.data[0]) || (Array.isArray(apiData) ? apiData[0] : null);
                        if (user) {
                            setUserId(user.userId || user.UserId);
                            setSiteId(user.siteId || user.SiteId);
                        }
                    }
                } catch (error) {
                    console.error("Failed to fetch user context", error);
                }
            }

            if (isEdit && pollIdToEdit) {
                try {
                    const pollRes = await apiServices({
                        method: 'GET',
                        url: `/ResidentialPollMaster/getResidentialPolls/${pollIdToEdit}`
                    });
                    
                    let pData = pollRes.data || pollRes;
                    if (pData?.data) pData = pData.data;
                    const apiData = Array.isArray(pData) ? pData[0] : null;

                    if (apiData) {
                        setFormData({
                            pollId: apiData.PollId,
                            optionId: apiData.OptionId,
                            title: apiData.Title,
                            description: apiData.Description,
                            pollType: apiData.PollType,
                            startDate: apiData.StartDate ? apiData.StartDate.substring(0, 16) : '',
                            endDate: apiData.EndDate ? apiData.EndDate.substring(0, 16) : '',
                            isAnonymous: apiData.IsAnonymous,
                            status: apiData.Status,
                            showResultsLive: apiData.ShowResultsLive
                        });

                        const loadedOptions = Array.isArray(apiData.Optionswithvote) 
                            ? apiData.Optionswithvote.map(o => o.OptionText) 
                            : [];
                        
                        if (loadedOptions.length > 0) {
                            setPollOptions(loadedOptions);
                        }
                    }
                } catch (error) {
                    console.error("Failed to load poll data", error);
                    toast.error("Failed to load poll data.");
                } finally {
                    setIsFetching(false);
                }
            }
        };
        fetchContext();
    }, [isEdit, pollIdToEdit]);

    const handleInputChange = (e) => {
        const { id, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSelectChange = (key, option) => {
        setFormData(prev => ({ ...prev, [key]: option ? option.value : null }));
    };

    const handleOptionChange = (index, value) => {
        const newOptions = [...pollOptions];
        newOptions[index] = value;
        setPollOptions(newOptions);
    };

    const addOption = () => {
        setPollOptions([...pollOptions, '']);
    };

    const removeOption = (index) => {
        const newOptions = [...pollOptions];
        newOptions.splice(index, 1);
        setPollOptions(newOptions);
    };

    const validateForm = () => {
        if (!formData.title) return toast.warning("Title is required"), false;
        if (!formData.pollType) return toast.warning("Poll Type is required"), false;
        if (!formData.description) return toast.warning("Description is required"), false;
        if (!formData.status) return toast.warning("Status is required"), false;
        if (!formData.startDate) return toast.warning("Start Date is required"), false;
        if (!formData.endDate) return toast.warning("End Date is required"), false;

        const validOptions = pollOptions.filter(o => o && o.trim().length > 0);
        if (validOptions.length < 2) {
            return toast.warning("Please add at least 2 valid options."), false;
        }

        if (new Date(formData.endDate) < new Date(formData.startDate)) {
            return toast.warning("End Date cannot be before Start Date"), false;
        }

        return true;
    };

    const performAddPoll = async (payload) => {
        try {
            const res = await apiServices({
                method: 'POST',
                url: `/ResidentialPollMaster/ResidentialCreatePoll`,
                data: payload
            });

            if (res.success || res.isSuccess || res.IsSuccess || res) {
                toast.success(isEdit ? 'Poll updated successfully!' : 'Poll created successfully!');
                navigate('/zentres/Poll-Management');
            } else {
                toast.error(res.message || 'Failed to process poll. Try again.');
                setIsLoading(false);
            }
        } catch (error) {
            console.error("Error adding poll", error);
            toast.error("Something went wrong!");
            setIsLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);

        const validOptions = pollOptions.filter(o => o && o.trim().length > 0);

        const payload = {
            pollId: isEdit ? (pollIdToEdit || 0) : 0,
            userId: userId,
            siteId: siteId,
            optionId: formData.optionId || 0,
            title: formData.title,
            description: formData.description,
            pollType: formData.pollType,
            startDate: formData.startDate,
            endDate: formData.endDate,
            isAnonymous: formData.isAnonymous,
            status: formData.status,
            showResultsLive: formData.showResultsLive,
            options: validOptions
        };

        if (isEdit) {
            // Edit Flow: Delete -> Recreate (matching exact Angular implementation)
            try {
                const delRes = await apiServices({
                    method: 'DELETE',
                    url: `/ResidentialPollMaster/deleteResidentialPoll/${pollIdToEdit}`
                });
                
                if (delRes.success || delRes.isSuccess || delRes.IsSuccess || delRes) {
                    await performAddPoll(payload);
                } else {
                    toast.error(delRes.message || 'Failed to overwrite existing poll.');
                    setIsLoading(false);
                }
            } catch (err) {
                console.error("Delete before update failed", err);
                toast.error("Failed to update poll due to deletion error.");
                setIsLoading(false);
            }
        } else {
            // Add Flow
            await performAddPoll(payload);
        }
    };

    if (isFetching) {
        return (
            <div className="bg-[#F8FAFC] px-6 py-4 flex flex-col rounded-lg flex-1 overflow-y-auto items-center justify-center">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl text-gray-400" />
            </div>
        );
    }

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading={isEdit ? 'Update Poll' : 'Create New Poll'}
                subHeading={isEdit ? 'Modify details and options of your poll' : 'Fill out the details below to broadcast a new poll'}
            />

            <div className="mt-6 w-full max-w-5xl flex flex-col gap-6 pb-8">
                {/* General Info Card */}
                <div className="bg-white p-6 rounded-2xl border border-gray-100 flex flex-col gap-6 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-800 tracking-tight">Poll Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        
                        <div className="flex flex-col gap-2 md:col-span-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block">Title <span className="text-red-500">*</span></label>
                            <input 
                                className="w-full px-3 py-2 bg-[#F8FAFC] border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:border-gray-400 transition-all placeholder:text-gray-400" 
                                type="text" 
                                id="title" 
                                placeholder="E.g., Swimming Pool Renovation"
                                value={formData.title} 
                                onChange={handleInputChange} 
                            />
                        </div>
                        
                        <div className="flex flex-col gap-2">
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 block uppercase tracking-wider">Poll Type <span className="text-red-500">*</span></span>}
                                options={[{label: 'Single Choice', value: 'single'}, {label: 'Multiple Choice', value: 'multiple'}]}
                                value={formData.pollType ? { label: formData.pollType === 'single' ? 'Single Choice' : 'Multiple Choice', value: formData.pollType } : null}
                                onChange={(opt) => handleSelectChange('pollType', opt)}
                                placeholder="Select Poll Type"
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <CustomSelect
                                label={<span className="text-xs font-bold text-gray-700 block uppercase tracking-wider">Status <span className="text-red-500">*</span></span>}
                                options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                                value={formData.status ? { label: formData.status, value: formData.status } : null}
                                onChange={(opt) => handleSelectChange('status', opt)}
                                placeholder="Select Status"
                            />
                        </div>

                        <div className="flex flex-col gap-2 md:col-span-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block">Description <span className="text-red-500">*</span></label>
                            <textarea 
                                className="w-full px-3 py-2 bg-[#F8FAFC] border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:border-gray-400 transition-all placeholder:text-gray-400 min-h-[100px]" 
                                id="description" 
                                placeholder="Provide context or details about this poll..."
                                value={formData.description} 
                                onChange={handleInputChange} 
                            ></textarea>
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block">Start Date <span className="text-red-500">*</span></label>
                            <input 
                                className="w-full px-3 py-2 bg-[#F8FAFC] border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:border-gray-400 transition-all placeholder:text-gray-400" 
                                type="datetime-local" 
                                id="startDate" 
                                value={formData.startDate} 
                                onChange={handleInputChange} 
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block">End Date <span className="text-red-500">*</span></label>
                            <input 
                                className="w-full px-3 py-2 bg-[#F8FAFC] border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:border-gray-400 transition-all placeholder:text-gray-400" 
                                type="datetime-local" 
                                id="endDate" 
                                value={formData.endDate} 
                                onChange={handleInputChange} 
                            />
                        </div>

                        <div className="flex flex-col gap-3 mt-2">
                            <label className="flex items-center gap-3 cursor-pointer group">
                                <input 
                                    type="checkbox" 
                                    id="isAnonymous"
                                    checked={formData.isAnonymous}
                                    onChange={handleInputChange}
                                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                                />
                                <span className="text-sm font-semibold text-gray-700 group-hover:text-blue-600 transition-colors">Anonymous Poll</span>
                            </label>
                            <label className="flex items-center gap-3 cursor-pointer group">
                                <input 
                                    type="checkbox" 
                                    id="showResultsLive"
                                    checked={formData.showResultsLive}
                                    onChange={handleInputChange}
                                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                                />
                                <span className="text-sm font-semibold text-gray-700 group-hover:text-blue-600 transition-colors">Show Results Live</span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Options Builder Card */}
                <div className="bg-white p-6 rounded-2xl border border-gray-100 flex flex-col gap-4 shadow-sm">
                    <div>
                        <h3 className="text-lg font-bold text-gray-800 tracking-tight">Poll Options</h3>
                        <p className="text-xs text-gray-500 mt-1">Provide the choices users can vote on. Minimum 2 options required.</p>
                    </div>

                    <div className="flex flex-col gap-3 mt-2">
                        {pollOptions.map((opt, index) => (
                            <div key={index} className="flex items-center gap-3">
                                <input 
                                    className="flex-1 px-3 py-2 bg-[#F8FAFC] border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:border-gray-400 transition-all placeholder:text-gray-400" 
                                    type="text" 
                                    placeholder={`Option ${index + 1}`}
                                    value={opt} 
                                    onChange={(e) => handleOptionChange(index, e.target.value)} 
                                />
                                <button 
                                    type="button"
                                    onClick={() => removeOption(index)}
                                    className="w-9 h-9 flex items-center justify-center shrink-0 rounded-md bg-red-50 text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                                >
                                    <FontAwesomeIcon icon={faTrash} className="text-sm" />
                                </button>
                            </div>
                        ))}

                        <button 
                            type="button" 
                            onClick={addOption}
                            className="w-max mt-2 px-4 py-2 border border-gray-200 bg-[#F8FAFC] text-gray-600 text-xs font-bold rounded-md hover:border-blue-500 hover:text-blue-600 transition-all flex items-center gap-2"
                        >
                            <FontAwesomeIcon icon={faPlus} /> Add Option
                        </button>

                        {pollOptions.filter(o => o.trim().length > 0).length < 2 && (
                            <span className="text-[10px] font-bold text-red-500 mt-1">* Minimum 2 valid options are required.</span>
                        )}
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="flex justify-between items-center mt-2">
                    <button
                        type="button"
                        onClick={() => navigate("/zentres/Poll-Management")}
                        className="px-6 py-2.5 border border-gray-200 rounded-xl text-xs font-bold flex items-center gap-2 text-gray-600 hover:bg-gray-50 active:scale-95 transition-all shadow-sm"
                    >
                        <FontAwesomeIcon icon={faAngleLeft} /> Cancel
                    </button>

                    <button
                        onClick={handleSubmit}
                        disabled={isLoading || pollOptions.filter(o => o.trim().length > 0).length < 2}
                        className="px-8 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200 disabled:opacity-50"
                        style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                        onMouseEnter={(e) => {
                            if (!isLoading) {
                                e.currentTarget.style.backgroundColor = primaryColor();
                                e.currentTarget.style.color = '#FFFFFF';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isLoading) {
                                e.currentTarget.style.backgroundColor = secondaryColor();
                                e.currentTarget.style.color = primaryColor();
                            }
                        }}
                    >
                        {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update Poll' : 'Submit Poll')}
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PollForm;
